/**
 * Carousel Slider Component with Keen Slider
 * Handles slider initialization with conditional activation, navigation, and autoplay
 */

// Ensure KeenSlider is available before defining custom element
function defineCarouselSlider() {
  if (!customElements.get('carousel-slider')) {
    class CarouselSlider extends HTMLElement {
    constructor() {
      super();
      this.slider = null;
      this.autoplayTimer = null;
    }

    readSettings() {
      // Get settings from data attributes
      this.slidesXl = parseFloat(this.dataset.slidesXl || '4');
      this.slidesDesktop = parseFloat(this.dataset.slidesDesktop || '3');
      this.slidesTablet = parseFloat(this.dataset.slidesTablet || '2');
      this.slidesMobile = parseFloat(this.dataset.slidesMobile || '1');
      this.loop = this.dataset.loop === 'true';
      this.centered = this.dataset.centered === 'true';
      this.showNavigation = this.dataset.showNavigation !== 'false';
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed || '5') * 1000;
      this.autoplayDirection = this.dataset.autoplayDirection || 'left';
      this.spacingDesktop = parseInt(this.dataset.spacingDesktop || '16');
      this.spacingTablet = parseInt(this.dataset.spacingTablet || '12');
      this.spacingMobile = parseInt(this.dataset.spacingMobile || '8');
    }

    connectedCallback() {
      // Wait for Keen Slider library to load
      if (typeof KeenSlider === 'undefined') {
        setTimeout(() => this.connectedCallback(), 100);
        return;
      }
      
      // Use requestAnimationFrame + setTimeout for better timing
      // This ensures DOM is fully ready and rendering has occurred
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.readSettings();
          this.initSlider();
          this.attachEventListeners();
        }, 0);
      });
    }

    disconnectedCallback() {
      if (this.slider) {
        this.slider.destroy();
      }
      this.clearAutoplay();
    }

    getCurrentBreakpoint() {
      const width = window.innerWidth;
      if (width >= 1800) return 'xl';
      if (width >= 1025) return 'desktop';
      if (width >= 750) return 'tablet';
      return 'mobile';
    } 

    getSlidesPerView() {
      const breakpoint = this.getCurrentBreakpoint();
      switch (breakpoint) {
        case 'xl':
          return this.slidesXl;
        case 'desktop':
          return this.slidesDesktop;
        case 'tablet':
          return this.slidesTablet;
        case 'mobile':
          return this.slidesMobile;
        default:
          return this.slidesMobile;
      }
    }

    getSpacing() {
      const breakpoint = this.getCurrentBreakpoint();
      switch (breakpoint) {
        case 'desktop':
          return this.spacingDesktop;
        case 'tablet':
          return this.spacingTablet;
        case 'mobile':
          return this.spacingMobile;
        default:
          return this.spacingMobile;
      }
    }

    shouldActivateSlider(totalSlides) {
      const slidesPerView = this.getSlidesPerView();
      return totalSlides > slidesPerView;
    }

    initSlider() {
      const sliderElement = this.querySelector('.keen-slider');
      if (!sliderElement) return;

      const slides = sliderElement.querySelectorAll('.keen-slider__slide');
      const totalSlides = slides.length;
      const slidesPerView = this.getSlidesPerView();


      // Check if we should show navigation (only if there are more slides than visible)
      const shouldShowNavigation = this.shouldActivateSlider(totalSlides);``
      
      if (shouldShowNavigation) {
        this.classList.add('slider-active');
        // Show/hide navigation based on setting
        if (!this.showNavigation) {
          this.hideNavigation();
        } else {
          this.showNavigationArrows();
        }
      } else {
        // Hide navigation if not enough slides to scroll
        this.hideNavigation();
        this.classList.remove('slider-active');
      }

      // Initialize Keen Slider with responsive settings
      // Only apply centering when there are enough slides to scroll
      const shouldCenter = this.centered && shouldShowNavigation;
      const enableLoop = shouldShowNavigation && this.loop;
      
      const sliderConfig = {
        loop: enableLoop,
        mode: 'snap',
        drag: shouldShowNavigation, // Only enable drag if there are enough slides
        initial: 0,
        defaultAnimation: {
          duration: 500
        },
        slides: {
          perView: this.slidesMobile,
          spacing: this.spacingMobile,
          origin: shouldCenter ? 'center' : 'auto',
        },
        breakpoints: {
          '(min-width: 750px)': {
            slides: {
              perView: this.slidesTablet,
              spacing: this.spacingTablet,
              origin: shouldCenter ? 'center' : 'auto',
            },
          },
          '(min-width: 1025px)': {
            slides: {
              perView: this.slidesDesktop,
              spacing: this.spacingDesktop,
              origin: shouldCenter ? 'center' : 'auto',
            },
          },
          '(min-width: 1800px)': {
            slides: {
              perView: this.slidesXl,
              spacing: this.spacingDesktop,
              origin: shouldCenter ? 'center' : 'auto',
            },
          },
        },
        created: (slider) => {
          // If centered + loop, ensure we start at first slide
          if (shouldCenter && enableLoop) {
            setTimeout(() => {
              slider.moveToIdx(0, true); // Move to first slide without animation
            }, 0);
          }
          
          if (this.autoplayEnabled) {
            this.startAutoplay();
          }
          // Update arrow states on creation
          this.updateArrowStates();
        },
        slideChanged: (slider) => {
          // Update arrow states when slide changes
          this.updateArrowStates();
          
          // Dispatch custom event for video cards to listen to
          const activeIndex = slider.track.details.rel;
          document.dispatchEvent(new CustomEvent('carousel:slideChanged', {
            detail: {
              slider: this,
              activeIndex: activeIndex
            }
          }));
        },
        dragStarted: () => {
          this.clearAutoplay();
        },
        dragEnded: () => {
          if (this.autoplayEnabled) {
            this.startAutoplay();
          }
        },
      };

      this.slider = new KeenSlider(sliderElement, sliderConfig);

      // Update arrow states immediately after initialization
      this.updateArrowStates();

      // Handle resize to check if slider should remain active
      this.handleResize();
    }

    updateArrowStates() {
      if (!this.slider) return;

      const prevBtn = this.querySelector('.slider-group__arrow--prev');
      const nextBtn = this.querySelector('.slider-group__arrow--next');

      if (!prevBtn || !nextBtn) return;

      const currentSlide = this.slider.track.details.rel;
      const totalSlides = this.slider.track.details.slides.length;
      const slidesPerView = this.getSlidesPerView();

      // If loop is enabled, arrows are never disabled
      if (this.loop && this.classList.contains('slider-active')) {
        prevBtn.classList.remove('slider-group__arrow--disabled');
        nextBtn.classList.remove('slider-group__arrow--disabled');
        return;
      }

      // Disable prev arrow if at the first slide
      if (currentSlide === 0) {
        prevBtn.classList.add('slider-group__arrow--disabled');
      } else {
        prevBtn.classList.remove('slider-group__arrow--disabled');
      }

      // Disable next arrow if at the last possible position
      // (when the last slide(s) are fully visible)
      const maxSlide = Math.max(0, totalSlides - Math.floor(slidesPerView));
      if (currentSlide >= maxSlide) {
        nextBtn.classList.add('slider-group__arrow--disabled');
      } else {
        nextBtn.classList.remove('slider-group__arrow--disabled');
      }
    }

    handleResize() {
      let resizeTimer;
      let lastWidth = window.innerWidth;
      
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          const currentWidth = window.innerWidth;
          
          // Only reinitialize if width changed (not height)
          if (currentWidth !== lastWidth) {
            lastWidth = currentWidth;
            
            // Reinitialize slider on resize to update slides per view and spacing
            if (this.slider) {
              this.slider.destroy();
              this.slider = null;
            }
            this.readSettings();
            this.initSlider();
          }
        }, 250);
      });
    }

    attachEventListeners() {
      // Previous arrow
      const prevBtn = this.querySelector('.slider-group__arrow--prev');
      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          if (this.slider) {
            this.slider.prev();
            this.clearAutoplay();
            if (this.autoplayEnabled) {
              this.startAutoplay();
            }
          }
        });
      }

      // Next arrow
      const nextBtn = this.querySelector('.slider-group__arrow--next');
      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          if (this.slider) {
            this.slider.next();
            this.clearAutoplay();
            if (this.autoplayEnabled) {
              this.startAutoplay();
            }
          }
        });
      }

      // Pause autoplay on hover
      if (this.autoplayEnabled) {
        this.addEventListener('mouseenter', () => {
          this.clearAutoplay();
        });

        this.addEventListener('mouseleave', () => {
          this.startAutoplay();
        });
      }

      // Pause autoplay when page is not visible
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.clearAutoplay();
        } else if (this.autoplayEnabled) {
          this.startAutoplay();
        }
      });
    }

    startAutoplay() {
      this.clearAutoplay();
      if (!this.slider || !this.autoplayEnabled) return;

      this.autoplayTimer = setInterval(() => {
        if (this.slider) {
          if (this.autoplayDirection === 'right') {
            this.slider.prev();
          } else {
            this.slider.next();
          }
        }
      }, this.autoplaySpeed);
    }

    clearAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    hideNavigation() {
      const arrows = this.querySelectorAll('.slider-group__arrow');
      arrows.forEach(arrow => arrow.style.display = 'none');
    }

    showNavigationArrows() {
      const arrows = this.querySelectorAll('.slider-group__arrow');
      arrows.forEach(arrow => arrow.style.display = '');
    }
  }

    customElements.define('carousel-slider', CarouselSlider);
  }
}

// Try to define immediately
defineCarouselSlider();

// Also try on DOMContentLoaded if not already defined
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', defineCarouselSlider);
} else {
  // DOM already loaded, define now
  defineCarouselSlider();
}
